import React from 'react';
import { isEmpty, keyBy } from 'lodash';

import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import InputAdornment from '~/components/core/Atomic/InputAdornment';
import MultiSelectField from '~/components/core/Molecules/Fields/MultiSelectField';
import CloseIcon from '~/components/icons/CloseIcon';
import type { Category } from '~/server_shared/swagger/data-contracts';

interface CategoryFilterProps<T extends Category> {
  id?: string;
  label?: string;
  categories: T[];
  selectedCategories: number[];
  onChange: (categoryIds: number[]) => void;
  disabled?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  className?: string;
  showEmptyCategory?: boolean;
  emptyCategoryLabel?: string;
}

const EMPTY_CATEGORY = {
  id: 0,
  label: 'No Category',
  key: 'empty_category',
  color: 'teal-600',
  is_disabled: false,
};

const CategoryFilter = <T extends Category>({
  id = 'categories',
  label = 'Category',
  categories,
  selectedCategories,
  onChange,
  disabled = false,
  isLoading = false,
  isError = false,
  className,
  showEmptyCategory = true,
  emptyCategoryLabel = 'No Category',
}: CategoryFilterProps<T>) => {
  const categoriesById: { [id: string]: T & typeof EMPTY_CATEGORY } = {
    ...keyBy(categories, 'id'),
    ...(showEmptyCategory
      ? {
          '0': {
            ...EMPTY_CATEGORY,
            label: emptyCategoryLabel,
          } as T & typeof EMPTY_CATEGORY,
        }
      : {}),
  };

  const isDisabled =
    disabled || isLoading || isError || (showEmptyCategory && Object.keys(categoriesById).length === 1); // Only empty category available

  return (
    <MultiSelectField<string>
      id={id}
      label={label}
      options={Object.keys(categoriesById)}
      disabled={isDisabled}
      renderValue={(selectedCategoriesIds) => {
        return selectedCategoriesIds
          .map((selectedCategoryId) => categoriesById[selectedCategoryId]?.label || '')
          .join(', ');
      }}
      renderOption={(categoryId) => categoriesById[categoryId]?.label || ''}
      fullWidth
      sortAlphabetic
      onChange={(values: string[]) => {
        onChange(values.map((v) => parseInt(v, 10)));
      }}
      value={selectedCategories.map(String)}
      className={className}
      InputProps={{
        endAdornment: isEmpty(selectedCategories) ? null : (
          <InputAdornment className="mr-24" position="start">
            <IconButton className="p-8" onClick={() => onChange([])}>
              <CloseIcon size={10} />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
};

export default CategoryFilter;
